/**
 * Browser-tab titles: "TalentHop - <page>" on every route, so a recruiter with six tabs open can
 * tell Candidates from Review. ONE map, applied on every navigation (routes.tsx subscribes), so a
 * new page only needs a line here — no per-page effect to forget.
 *
 * Patterns are matched in order against the pathname; `:param` segments match any one segment.
 * Anything unmatched (redirect stubs, the 404 catch-all) falls back to the bare brand.
 */
const BRAND = "TalentHop";

const TITLES: [pattern: string, page: string][] = [
  // public / auth
  ["/login", "Sign in"],
  ["/signup", "Sign up"],
  ["/verify-signup", "Verify your email"],
  ["/approve-signup", "Approve signup"],
  ["/app/activity", "Activity"],
  ["/set-password", "Set password"],
  ["/reset-password", "Reset password"],
  ["/forgot-password", "Forgot password"],
  ["/interview/:token", "Your interview"],
  ["/interview", "Your interview"],
  ["/find-my-link", "Find my interview link"],
  ["/complete-profile/:token", "Complete your application"],
  ["/jobs", "Open roles"],
  ["/jobs/:slug", "Job"],
  ["/careers/:companySlug", "Open roles"],
  ["/shared/:token", "Shared report"],
  ["/speech-check", "Speech check"],

  // recruiter app
  ["/app", "Dashboard"],
  ["/app/new", "New requirement"],
  ["/app/requisitions", "Requirements"],
  ["/app/requisitions/:id", "Requirement"],
  ["/app/candidates", "Candidates"],
  ["/app/resumes", "Candidates"],
  ["/app/interviews", "Interviews"],
  ["/app/inbox", "Inbox"],
  ["/app/network", "Recruitment network"],
  ["/app/connections", "Connections"],
  ["/app/review/:candidacyId", "Candidate review"],
  ["/app/plans", "Plans & billing"],
  ["/app/partner", "Partner"],
  ["/app/partner/orgs", "Partner orgs"],
  ["/app/team", "Team"],
  ["/app/profile", "Profile"],
  ["/app/security", "Account security"],

  // platform admin
  ["/admin", "Admin"],
  ["/admin/plans", "Admin · Plans"],
  ["/admin/credit-pricing", "Admin · Credit pricing"],
  ["/admin/spend", "Admin · Spend"],
  ["/admin/billing", "Admin · Revenue"],
  ["/admin/payments", "Admin · Payments"],
  ["/admin/orgs", "Admin · Client orgs"],
  ["/admin/orgs/:orgId", "Admin · Client org"],
  ["/admin/signup-requests", "Admin · Signup requests"],
  ["/admin/partners", "Admin · Partners"],
  ["/admin/sourcing", "Admin · Sourcing"],
  ["/admin/regions", "Admin · Regions"],
  ["/admin/verticals", "Admin · Verticals"],
  ["/admin/taxonomy", "Admin · Taxonomy"],
  ["/admin/speech-quality", "Admin · Speech quality"],
  ["/admin/interview-languages", "Admin · Interview languages"],
  ["/admin/models", "Admin · Models"],
  ["/admin/prompts", "Admin · Model prompts"],
  ["/admin/ai-models", "Admin · AI models"],
  ["/admin/judge-calls", "Admin · Judge calls"],
  ["/admin/email-templates", "Admin · Email templates"],
  ["/admin/providers", "Admin · Providers"],
  ["/admin/connectors", "Admin · Connectors"],
  ["/admin/invite-modes", "Admin · Invite modes"],
  ["/admin/releases", "Admin · Releases"],
  ["/admin/team", "Admin · Team"],
  ["/admin/audit", "Admin · Audit log"],
  ["/admin/dead-letters", "Admin · Dead letters"],
  ["/admin/api-errors", "Admin · API errors"],
  ["/admin/feedback", "Admin · Feedback"],
  ["/admin/compliance", "Admin · Compliance"],
  ["/admin/security", "Admin · Account security"],
];

function matches(pattern: string, path: string): boolean {
  const p = pattern.split("/").filter(Boolean);
  const a = path.split("/").filter(Boolean);
  return p.length === a.length && p.every((seg, i) => seg.startsWith(":") || seg === a[i]);
}

/** "TalentHop - Candidates" for /app/candidates; "TalentHop" when the path isn't a known page. */
export function pageTitleFor(pathname: string): string {
  const path = pathname.replace(/\/+$/, "") || "/";
  const hit = TITLES.find(([pattern]) => matches(pattern, path));
  return hit ? `${BRAND} - ${hit[1]}` : BRAND;
}
